import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { usePageMetadata } from "@/hooks/use-page-metadata";
import { Plus, Edit, FileText, ExternalLink, Check, X } from "lucide-react";
import PageMetadataForm from "@/components/admin/PageMetadataForm";
import { useNavigate } from "react-router";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { routes } from "@/lib/routes";

export default function AdminCMS() {
  const { HelmetTags } = usePageMetadata("/admin/cms");
  const navigate = useNavigate();
  const [view, setView] = useState<"list" | "edit" | "create">("list");
  const [selectedPage, setSelectedPage] = useState<any>(null);
  const [selectedPagePath, setSelectedPagePath] = useState<string>("");

  const allPagesMetadata = useQuery((api as any).pageMetadata.getAllPageMetadata);

  // Merge routes with metadata
  const mergedPages = routes.map(route => {
    const metadata = allPagesMetadata?.find((p: any) => p.pagePath === route.path);
    return {
      pagePath: route.path,
      title: metadata?.title || route.name,
      hasMetadata: !!metadata,
      lastUpdated: metadata?._creationTime,
      metadata: metadata
    };
  });

  // Also add any pages in metadata that aren't in routes (dynamic pages)
  if (allPagesMetadata) {
    allPagesMetadata.forEach((meta: any) => {
      if (!mergedPages.find(p => p.pagePath === meta.pagePath)) {
        mergedPages.push({
          pagePath: meta.pagePath,
          title: meta.title || "Unknown Page",
          hasMetadata: true,
          lastUpdated: meta._creationTime,
          metadata: meta
        });
      }
    });
  }

  const handleEdit = (page: any) => {
    setSelectedPage(page.metadata);
    setSelectedPagePath(page.pagePath);
    setView("edit");
  };

  const handleCreate = () => {
    setSelectedPage(null);
    setSelectedPagePath("");
    setView("create");
  };

  const handleBack = () => {
    setView("list");
    setSelectedPage(null);
    setSelectedPagePath("");
  };

  return (
    <>
      <HelmetTags />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen"
      >
        <Navbar />
        <main className="pt-32 pb-16 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-4xl font-bold tracking-tight mb-2">CMS Management</h1>
                <p className="text-muted-foreground">Manage page metadata and content</p>
              </div>
              {view === "list" && (
                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => navigate("/admin/cms/blogs")}>
                    <FileText className="h-4 w-4 mr-2" />
                    Manage Blogs
                  </Button>
                  <Button onClick={handleCreate}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Page
                  </Button>
                </div>
              )}
            </div>
            
            {view === "list" ? (
              <Card>
                <CardHeader>
                  <CardTitle>Pages</CardTitle>
                  <CardDescription>List of all pages. Edit metadata to improve SEO.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Page Path</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Updated</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mergedPages.map((page: any) => (
                        <TableRow key={page.pagePath}>
                          <TableCell className="font-medium">{page.pagePath}</TableCell>
                          <TableCell className="max-w-md truncate">{page.title}</TableCell>
                          <TableCell>
                            {page.hasMetadata ? (
                              <span className="flex items-center text-green-600 text-xs font-medium">
                                <Check className="h-3 w-3 mr-1" /> Managed
                              </span>
                            ) : (
                              <span className="flex items-center text-muted-foreground text-xs">
                                <X className="h-3 w-3 mr-1" /> Unmanaged
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {page.lastUpdated ? new Date(page.lastUpdated).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(page)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <a href={page.pagePath} target="_blank" rel="noreferrer">
                              <Button variant="ghost" size="icon">
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </a>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <PageMetadataForm 
                    initialData={selectedPage} 
                    pagePath={selectedPagePath}
                    onBack={handleBack}
                    onSave={handleBack}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </main>
        <Footer />
      </motion.div>
    </>
  );
}